import { Vector3 } from 'three';
import {
  TrackDef,
  spanControlPoints,
  evalSpan,
  evalSpanDeriv,
  spanCount,
  spanCurvature,
  spanCurvatureVector,
} from './spline';

const SAMPLES_PER_SPAN = 32;

/**
 * Arc-length lookup table over the whole chain. The Bézier parameter u is not
 * proportional to distance, so we tabulate: dense samples per span, cumulative
 * chord length, then binary search + linear interpolation for s → (span, u).
 */
export class Centerline {
  readonly track: TrackDef;
  readonly totalLength: number;
  /** cumulative s at each sample; sample j of span i is at index i*SAMPLES_PER_SPAN + j */
  private readonly cumS: Float64Array;
  private readonly nSpans: number;
  private readonly cps: [Vector3, Vector3, Vector3, Vector3][];

  constructor(track: TrackDef) {
    this.track = track;
    this.nSpans = spanCount(track);
    this.cps = [];
    for (let i = 0; i < this.nSpans; i++) this.cps.push(spanControlPoints(track, i));

    const n = this.nSpans * SAMPLES_PER_SPAN + 1;
    this.cumS = new Float64Array(Math.max(n, 1));
    let s = 0;
    const prev = new Vector3();
    const cur = new Vector3();
    let idx = 0;
    if (this.nSpans > 0) {
      evalSpan(this.cps[0], 0, prev);
      this.cumS[idx++] = 0;
      for (let i = 0; i < this.nSpans; i++) {
        for (let j = 1; j <= SAMPLES_PER_SPAN; j++) {
          const u = j / SAMPLES_PER_SPAN;
          evalSpan(this.cps[i], u, cur);
          s += cur.distanceTo(prev);
          this.cumS[idx++] = s;
          prev.copy(cur);
        }
      }
    }
    this.totalLength = s;
  }

  /** Wrap (closed) or clamp (open) an arc-length value into [0, totalLength]. */
  normalizeS(s: number): number {
    const L = this.totalLength;
    if (L <= 0) return 0;
    if (this.track.closed) {
      s = s % L;
      if (s < 0) s += L;
      return s;
    }
    return Math.min(Math.max(s, 0), L);
  }

  /** s → (span index, Bézier u). */
  sToSpanU(s: number): { span: number; u: number } {
    s = this.normalizeS(s);
    const arr = this.cumS;
    // binary search for the last index with cumS[idx] <= s
    let lo = 0;
    let hi = arr.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (arr[mid] <= s) lo = mid;
      else hi = mid - 1;
    }
    if (lo >= arr.length - 1) {
      return { span: this.nSpans - 1, u: 1 };
    }
    const segLen = arr[lo + 1] - arr[lo];
    const f = segLen > 1e-12 ? (s - arr[lo]) / segLen : 0;
    const sampleIdx = lo + f;
    const span = Math.min(Math.floor(sampleIdx / SAMPLES_PER_SPAN), this.nSpans - 1);
    const u = sampleIdx / SAMPLES_PER_SPAN - span;
    return { span, u: Math.min(Math.max(u, 0), 1) };
  }

  /** (span, u) → s, the inverse mapping (linear interpolation over the same table). */
  spanUToS(span: number, u: number): number {
    const sampleIdx = span * SAMPLES_PER_SPAN + u * SAMPLES_PER_SPAN;
    const lo = Math.min(Math.floor(sampleIdx), this.cumS.length - 2);
    const f = sampleIdx - lo;
    return this.cumS[lo] + f * (this.cumS[lo + 1] - this.cumS[lo]);
  }

  posAt(s: number, out = new Vector3()): Vector3 {
    const { span, u } = this.sToSpanU(s);
    return evalSpan(this.cps[span], u, out);
  }

  /** Unit tangent at s. */
  tanAt(s: number, out = new Vector3()): Vector3 {
    const { span, u } = this.sToSpanU(s);
    evalSpanDeriv(this.cps[span], u, out);
    const len = out.length();
    if (len < 1e-9) out.set(1, 0, 0);
    else out.divideScalar(len);
    return out;
  }

  /** Curvature magnitude (1/m) at s. */
  curvatureAt(s: number): number {
    const { span, u } = this.sToSpanU(s);
    return spanCurvature(this.cps[span], u);
  }

  /** Curvature vector at s (points toward the osculating centre, |v| = κ). */
  curvatureVecAt(s: number, out = new Vector3()): Vector3 {
    const { span, u } = this.sToSpanU(s);
    return spanCurvatureVector(this.cps[span], u, out);
  }

  /** Arc length at the start of each span — useful for mapping nodes to s. */
  spanStartS(span: number): number {
    return this.cumS[span * SAMPLES_PER_SPAN];
  }
}
